import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactElement, type ReactNode, useState } from 'react';
import { getAccessToken, refreshSession, useAuthStore } from '@/entities/user';
import { createAuthRefreshLink, createTrpcLinks, trpc } from '@/shared/api';

const QUERY_STALE_TIME_MS = 30_000;
const QUERY_RETRY_COUNT = 1;

export interface AppProvidersProps {
  readonly children: ReactNode;
}

/**
 * TanStack Query + tRPC 클라이언트 컨텍스트.
 * 세션이 만료돼 로그아웃되면 세션과 함께 쿼리 캐시를 비운다 — 회원별 조회 결과가 다음 회원에게 남지 않게 한다
 * (명시적 로그아웃은 useSignOut 이 비운다).
 */
export const AppProviders = ({ children }: AppProvidersProps): ReactElement => {
  // 클라이언트 인스턴스는 렌더마다 새로 만들면 캐시가 초기화되므로 상태로 고정한다.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: QUERY_STALE_TIME_MS,
            retry: QUERY_RETRY_COUNT,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        // 앱 전역 단일 재발급기(refreshSession)를 공유해 단일 비행이 전 경로에 성립한다.
        createAuthRefreshLink({
          refresh: refreshSession,
          onSessionExpired: () => {
            useAuthStore.getState().clear();
            queryClient.clear();
          },
        }),
        ...createTrpcLinks(getAccessToken),
      ],
    }),
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
};
